import { notification, Input } from 'antd';
import moment from 'moment';
import { Fragment, PureComponent } from 'react';
import { batch, connect } from 'react-redux';
import { CalculatorEntity } from '../../../../store/types/prescription';
import { AntTooltip } from '../../../common/AntTooltip';
import { CalculatorDate, MILISECONDS_IN_DAY } from './CalculatorDate';
import { CalculatorInput } from './CalculatorInput';
import { CalculatorInputAndSelect } from './CalculatorInputAndSelect';
import CalculatorInterPretationTable from './CalculatorInterpretationTable';
import { CalculatorSelect } from './CalculatorSelect';
import { ArgsTypeWithCustomKeys, CalculatorConfigType } from './calculatorsTypes';
import { CalculatorMetaDataType } from './padV2/CalculatorResults';
import { EkaFocosableElement } from '../../../common/types/EkaFocusable';
import uniq from 'lodash/uniq';

export interface ValueObject {
    [key: string]: number | undefined;
}

export interface UnitObject {
    [key: string]: string | undefined;
}

interface ObjectType {
    [k: string]: string;
}

type ctxConfigObject = {
    [key: string]: string;
};

export const requiredMethodsForEquationEvaluation: {
    [key: string]: any;
} = {
    one_way: (formula: string) => {
        return formula;
    },
    log: (num: number) => {
        return Math.log(num);
    },
    roundOff: (num: number) => {
        return Math.round((num + Number.EPSILON) * 10000) / 10000;
    },
    max: (num1: number, num2: number) => {
        return Math.max(num1, num2);
    },
    min: (num1: number, num2: number) => {
        return Math.min(num1, num2);
    },
    pow: (num1: number, num2: number) => {
        return Math.pow(num1, num2);
    },
    sqrt: (num: number) => {
        return Math.sqrt(num);
    },
    daysToDate: (days: string) => {
        const date = moment(new Date(Number(days) * MILISECONDS_IN_DAY));
        return date.isValid() ? date.format("DD MMM'YY").toString() : '';
    },
};

export type DefaultValuesForCalculator = {
    [key: string]: { value: string | undefined };
};

type Props = {
    calculatorType: CalculatorMetaDataType;
    onElementUpdate: (
        obj: CalculatorEntity,
        index?: number,
        itemIdx?: number,
        valueObject?: ValueObject,
        unitObject?: UnitObject,
    ) => void;
    closeMe: () => void;
    defaultValuesForCalculator: DefaultValuesForCalculator;
    defaultUnitsForCalculator?: {
        [k: string]: string;
    };
};

type State = {
    config: CalculatorConfigType | undefined;
    error: { [key: string]: string };
    results: CalculatorEntity[] | undefined;
    tableActiveSection: 'interpretation' | 'how-to-use';
};

class Calculator extends PureComponent<Props, State> {
    REF_MAP: (EkaFocosableElement | Input | null)[][] = [];
    state: State = {
        config: undefined,
        error: {},
        results: undefined,
        tableActiveSection: 'interpretation',
    };

    componentDidMount = async (): Promise<void> => {
        const { calculatorType } = this.props;
        try {
            const response = await fetch(calculatorType.api);
            const data = await response.json();
            const tableActiveSection = data.calculator.ekaInterpretation
                ? 'interpretation'
                : 'how-to-use';
            this.setState({ config: data, tableActiveSection });
            this.buildConfig(data);
        } catch (error) {
            console.log(error);
        }

        this.setDefaultValuesForCalculator();
        this.handleNextFocus(0, 0);
    };

    setDefaultValuesForCalculator = () => {
        const { config } = this.state;
        const { defaultValuesForCalculator, defaultUnitsForCalculator } = this.props;
        const argsKeys = Object.keys(this.state.config?.calculator?.definition?.args || []);

        argsKeys?.map((key) => {
            const arg = config?.calculator?.definition?.args?.[key];
            const useRoundedDefaultValue = arg?.useRoundedDefaultValue;

            if (arg?.valueSelectId) {
                const defaultSelectId =
                    defaultValuesForCalculator[arg?.ekaAutoFillSectionId || '']?.value;

                if (!defaultSelectId) {
                    return;
                }

                const valueSelects =
                    config?.calculator?.definition?.valueSelects[arg?.valueSelectId || ''];

                const optionValue = valueSelects?.find(
                    (valOption) =>
                        valOption.ekaSelectId?.toLowerCase() === defaultSelectId?.toLowerCase(),
                )?.value;

                optionValue &&
                    this.onChange(key, optionValue, 'valueSelects', useRoundedDefaultValue);
                return;
            }

            const inputValue = defaultValuesForCalculator[arg?.ekaAutoFillSectionId || '']?.value;
            inputValue && this.onChange(key, inputValue, undefined, useRoundedDefaultValue);
            if (arg && defaultUnitsForCalculator?.[arg?.ekaAutoFillSectionId || '']) {
                this.onChange(
                    key,
                    defaultUnitsForCalculator?.[arg?.ekaAutoFillSectionId || ''],
                    'unitSwitchers',
                    useRoundedDefaultValue,
                );
            }
        });
    };

    setInterpretationSectionActive = () => {
        this.setState({ tableActiveSection: 'interpretation' });
    };

    setHowToUseSectionActive = () => {
        this.setState({ tableActiveSection: 'how-to-use' });
    };

    onSetResult = (results: CalculatorEntity[]) => {
        const { config } = this.state;

        const args = config?.calculator?.definition?.args;
        const readOnlyField = this.getReadOnlyFields();
        const filledResults = results.filter((result, i) => {
            if (args?.[readOnlyField[i]].fieldType === 'DATE') {
                return result.value.qt ? true : false;
            } else {
                return isNaN(Number(result.value.qt)) ? false : true;
            }
        });

        const callback = () => config && this.buildConfig(config);
        this.setState({ results: filledResults }, callback);
    };

    onSaveInDb = () => {
        const { results } = this.state;
        const { closeMe, onElementUpdate } = this.props;
        const { config } = this.state;

        const args = config?.calculator?.definition?.args;
        const readOnlyField = this.getReadOnlyFields();

        if (!results?.length) {
            notification.error({
                message: `Results not Saved. Please fill all Sections Correctly.`,
                placement: 'topRight',
                duration: 5,
            });
            return;
        }

        batch(() => {
            results.forEach((res, i) => {
                const calcObj = {
                    ...res,
                    value: {
                        ...res.value,
                        qt: (args?.[readOnlyField[i]].fieldType === 'DATE'
                            ? res?.value?.qt
                            : Number(res?.value?.qt) === 0
                            ? 0
                            : +requiredMethodsForEquationEvaluation.roundOff(
                                  Number(res?.value?.qt),
                              ) || ''
                        ).toString(),
                        unit: this.defaultUnitForFinalValue(args?.[readOnlyField[i]]) || '',
                    },
                };
                const valueObject = config ? this.getSectionsValues(config) : {};
                const unitObject = config ? this.getSectionsUnits(config) : {};
                onElementUpdate(calcObj, undefined, i, valueObject, unitObject);
            });
        });
        closeMe();
    };

    buildConfig = (config: CalculatorConfigType) => {
        let ctxConf = config?.calculator?.definition?.args;

        const ctx = {
            hideVariables: (...variables: string[]) => {
                const config = Object.keys(ctxConf).reduce((prevObj, key) => {
                    if (variables.includes(key)) {
                        return { ...prevObj, [key]: { ...ctxConf[key], ekaHidden: true } };
                    }

                    return { ...prevObj, [key]: { ...ctxConf[key], ekaHidden: false } };
                }, {});

                ctxConf = config;
            },
            addHtml: (message: string, configObj: ctxConfigObject) => {
                if (configObj?.ekaAfterVariable) {
                    const config = Object.keys(ctxConf).reduce((prevObj, key) => {
                        if (key === configObj.ekaAfterVariable) {
                            return {
                                ...prevObj,
                                [key]: { ...ctxConf[key], ekaAfterVariable: message },
                            };
                        }

                        return {
                            ...prevObj,
                            [key]: { ...ctxConf[key], ekaAfterVariable: undefined },
                        };
                    }, {});

                    ctxConf = config;
                } else {
                    const resultDescriptionDiv = document.getElementById('result-description');
                    if (resultDescriptionDiv) resultDescriptionDiv.innerHTML = message;
                }
            },
            setDefault: (variableName: string, arg: any) => {
                ctxConf[variableName].inputValue = arg;
            },
            getNumberValue: (variableName: string) => {
                const { config, results } = this.state;
                const { getReadOnlyFields } = this;
                const readOnlyFields = getReadOnlyFields();
                const resultObject = readOnlyFields.reduce((acc: ObjectType, key, index) => {
                    if (results?.[index]?.value?.qt) acc[key] = results[index].value.qt;
                    return acc;
                }, {});
                const arg = config?.calculator?.definition?.args[variableName];
                const value = Number(resultObject[variableName] || arg?.default || arg?.inputValue);
                return value;
            },
            getUnit: (variableName: string) => {
                const { config } = this.state;
                const arg = config?.calculator?.definition?.args[variableName];
                return arg?.unitDefault;
            },
            showVariables: (...variables: string[]) => {
                const config = Object.keys(ctxConf).reduce((prevObj, key) => {
                    if (variables.includes(key)) {
                        return { ...prevObj, [key]: { ...ctxConf[key], ekaHidden: false } };
                    }

                    return {
                        ...prevObj,
                        [key]: { ...ctxConf[key], ekaHidden: true },
                    };
                }, {});

                ctxConf = config;
            },
        };

        const omni = {
            onResult: (variableArray: string[], cb: any) => {
                if (variableArray.length > 0) {
                    const args = variableArray.filter((key) => valueObject[key]);

                    if (variableArray.length === args.length) {
                        cb(ctx, ...args);
                    }
                } else {
                    cb(ctx);
                }
            },
            define: (fnName: string, fn: any) => {
                requiredMethodsForEquationEvaluation[fnName] = fn;
            },
            onInit: (cb: any) => {
                cb(ctx);
            },
        };

        const valueObject = config ? this.getSectionsValues(config) : {};

        config && eval(config?.calculator.definition.customJs);

        this.setState({
            config: {
                ...config,
                calculator: {
                    ...config?.calculator,
                    definition: {
                        ...config?.calculator?.definition,
                        args: ctxConf,
                    },
                },
            },
        });
    };

    updateFinalResults = () => {
        const { config } = this.state;
        const args = config?.calculator?.definition?.args;
        const { calculatorType } = this.props;
        const { result } = calculatorType;

        const readOnlyField = this.getReadOnlyFields();
        const valueObject = config ? this.getSectionsValues(config) : {};

        config && this.buildConfig(config);

        const isError = this.checkIfError(config, readOnlyField, valueObject);

        let equations = config?.calculator?.definition?.equations;
        const { equationKey, equationMapping } = config?.calculator?.definition || {};

        if (args && equationKey && equationMapping) {
            const value = args[equationKey]?.default || '';
            const equation = equationMapping[value];
            equations = equations?.filter((i) => i.name === equation);
        }

        const keys: {
            [key: string]: { value: string } | undefined;
        } = {};

        equations?.forEach((e) => {
            const name = e.name.split('-');
            const isValueEmpty = Object.keys(args || [])
                ?.filter((argName) => {
                    if (readOnlyField.find((rfield) => rfield === argName)) {
                        return false;
                    }

                    return !args?.[argName]?.ekaHidden;
                })
                .filter((value) => valueObject[value] === undefined);

            if (!isValueEmpty.length) {
                name.filter((argName) =>
                    readOnlyField.find((rfield) => rfield === argName),
                ).forEach((argName) => {
                    const value = Function(
                        `{ ${Object.keys({
                            ...valueObject,
                            ...requiredMethodsForEquationEvaluation,
                        }).join(', ')} }`,
                        `return ${e.f[argName]}`,
                    )({
                        ...valueObject,
                        ...requiredMethodsForEquationEvaluation,
                    });
                    keys[argName] = isError
                        ? undefined
                        : {
                              value: value,
                          };
                });
            }
        });

        const res: CalculatorEntity[] = [];

        Object.keys(keys).length !== 0 &&
            Object.keys(keys).forEach((argName) => {
                const keyValue = keys[argName];

                keyValue &&
                    calculatorType &&
                    res.push({
                        id: result.id,
                        name: result.name,
                        dis_name: result.name,
                        value: {
                            qt:
                                args?.[argName].fieldType === 'DATE'
                                    ? requiredMethodsForEquationEvaluation.daysToDate(
                                          keyValue.value,
                                      )
                                    : keyValue.value,
                            unit: this.defaultUnitForFinalValue(args?.[argName]) || '',
                        },
                    });
            });

        this.onSetResult(res);
    };

    defaultUnitForFinalValue = (arg: ArgsTypeWithCustomKeys | undefined) => {
        if (arg) {
            const unitSwitcher = this.state.config?.calculator?.definition?.unitSwitchers[
                arg?.unitSwitcherId
            ];
            const unitSwitcherOptions = unitSwitcher
                ?.map((item) => ({
                    ...item,
                    id: item.slug,
                    value: item.name,
                }))
                .filter((item) => item.availability !== 'NOT_BY_DEFAULT');
            return unitSwitcherOptions?.find((opt) => arg?.unitDefault === opt.slug)?.value;
        }
        return undefined;
    };

    setConfigArgsValue = (
        names: string[],
        sectionName: string,
        value: (string | undefined)[],
        nextFunc?: () => void,
    ) => {
        const result = names.reduce((pre, name: string, i: number) => {
            return {
                ...pre,
                calculator: {
                    ...pre?.calculator,
                    definition: {
                        ...pre?.calculator?.definition,
                        args: {
                            ...pre?.calculator?.definition?.args,
                            [name]: {
                                ...pre?.calculator?.definition?.args?.[name],
                                [sectionName]: value[i],
                            },
                        },
                    },
                },
            };
        }, this.state.config as CalculatorConfigType);

        this.setState({ config: result }, nextFunc);
    };

    convertPreviousUnitValueToPresent = (
        name: string,
        prevUnitDefault: string,
        presentUnitDefault: string,
        config: CalculatorConfigType,
    ): string => {
        const args = config?.calculator?.definition?.args;

        const unitSwitchers =
            config?.calculator?.definition?.unitSwitchers[args[name].unitSwitcherId];

        const prevUnitSwitcherOption = unitSwitchers.find((unit) => unit.slug === prevUnitDefault);
        const presentUnitSwitcherOption = unitSwitchers.find(
            (unit) => unit.slug === presentUnitDefault,
        );
        if (args[name]?.unitSwitcherReversed) {
            const v = Number(args[name]?.inputValue);
            const r = Number(eval(prevUnitSwitcherOption?.out || ''));
            const result = eval(presentUnitSwitcherOption?.in || '');
            return result;
        } else {
            const r = Number(args[name]?.inputValue);
            const v = Number(eval(prevUnitSwitcherOption?.in || ''));
            const result = eval(presentUnitSwitcherOption?.out || '');
            return result;
        }
    };

    getSectionsValues(config: CalculatorConfigType) {
        const args = config?.calculator?.definition?.args;

        const result: { [key: string]: number | undefined } = {};

        for (const key in args) {
            let inputValue = args[key].inputValue;

            if (args[key]?.default || args[key]?.valueSelectId) {
                inputValue = args[key].default;
            } else if (args[key]?.baseUnit || args[key]?.unitDefaultImperial) {
                inputValue = this.convertPreviousUnitValueToPresent(
                    key,
                    args[key].unitDefault,
                    args[key].baseUnit || args[key]?.unitDefaultImperial,
                    config,
                );
            }

            result[key] = !inputValue || isNaN(Number(inputValue)) ? undefined : Number(inputValue);
        }
        return result;
    }

    getSectionsUnits(config: CalculatorConfigType) {
        const args = config?.calculator?.definition?.args;
        const result: { [key: string]: string | undefined } = {};
        for (const key in args) {
            const unitValue = args[key].unitDefault;
            result[key] = unitValue;
        }
        return result;
    }

    checkIfError = (
        config: CalculatorConfigType | undefined,
        readOnlyField: string[],
        valueObject: ValueObject,
    ) => {
        const errorKeys: { [key: string]: string } = {};
        config?.calculator.definition.conditions.forEach((condObj) => {
            const isError = condObj.variables.every((v) => {
                if (!valueObject[v] && isNaN(Number(valueObject[v]))) {
                    return false;
                }

                return true;
            });

            if (isError) {
                condObj.variables.forEach((v) => {
                    const evalResult = Function(
                        Object.keys({ ...valueObject }).join(','),
                        `return ${condObj.f}`,
                    )(...Object.values({ ...valueObject }));

                    if (!evalResult && !readOnlyField.find((name) => name === v)) {
                        errorKeys[v] = condObj.name;
                    }
                });
            }
        });

        this.setState({ error: errorKeys });

        if (Object.keys(errorKeys).length === 0) {
            return false;
        }

        return true;
    };

    onChange = (
        name: string,
        value: string | undefined,
        type?: 'unitSwitchers' | 'valueSelects',
        useRoundedDefaultValue?: boolean,
    ) => {
        const { config } = this.state;
        const args = config?.calculator?.definition?.args;

        if (type === 'valueSelects') {
            this.setConfigArgsValue([name], 'default', [value], () => {
                this.updateFinalResults();
            });
            return;
        }

        if (type === 'unitSwitchers') {
            const prevUnitDefault = args?.[name]?.unitDefault || '';

            this.setConfigArgsValue([name], 'unitDefault', [value], () => {
                if (!args?.[name]?.inputValue) {
                    return;
                }
                const inputValue = config
                    ? value &&
                      this.convertPreviousUnitValueToPresent(name, prevUnitDefault, value, config)
                    : '';

                const inputValueToSet =
                    useRoundedDefaultValue && inputValue
                        ? Math.round(parseFloat(inputValue)).toString()
                        : inputValue;

                this.setConfigArgsValue([name], 'inputValue', [inputValueToSet], () => {
                    this.updateFinalResults();
                });
            });
        } else {
            this.setConfigArgsValue([name], 'inputValue', [value], () => {
                this.updateFinalResults();
            });
        }
    };

    setPropertyInputRef = (
        ref: EkaFocosableElement | Input | null,
        index: number,
        subIndex: number,
    ) => {
        if (!this.REF_MAP[index]) {
            this.REF_MAP[index] = [];
        }

        this.REF_MAP[index][subIndex] = ref;
    };

    INPUT_TYPE_CONFIG: {
        [k: string]: (
            argsKey: string,
            config: CalculatorConfigType,
            onChange: (
                name: string,
                value: string | undefined,
                type?: 'unitSwitchers' | 'valueSelects',
            ) => void,
            index: number,
            unitPrefix?: string,
        ) => JSX.Element;
    } = {
        DEFAULT: (
            argsKey: string,
            config: CalculatorConfigType,
            onChange: (
                name: string,
                value: string | undefined,
                type?: 'unitSwitchers' | 'valueSelects',
            ) => void,
            index: number,
            unitPrefix?: string,
        ) => {
            const args = config?.calculator?.definition?.args;

            if (args[argsKey]?.unitSwitcherId) {
                return (
                    <CalculatorInputAndSelect
                        argsKey={argsKey}
                        config={config}
                        onValueChange={onChange}
                        setPropertyInputRef={this.setPropertyInputRef}
                        index={index}
                        handleNextFocus={this.handleNextFocus}
                        unitPrefix={unitPrefix}
                    />
                );
            }

            if (args[argsKey]?.valueSelectId) {
                return (
                    <CalculatorSelect
                        argsKey={argsKey}
                        config={config}
                        onValueChange={onChange}
                        setPropertyInputRef={this.setPropertyInputRef}
                        index={index}
                        handleNextFocus={this.handleNextFocus}
                    />
                );
            }

            return (
                <CalculatorInput
                    arg={args[argsKey]}
                    onInputChange={onChange}
                    setPropertyInputRef={this.setPropertyInputRef}
                    index={index}
                    handleNextFocus={this.handleNextFocus}
                />
            );
        },
        DATE: (
            argsKey: string,
            config: CalculatorConfigType,
            onChange: (
                name: string,
                value: string | undefined,
                type?: 'unitSwitchers' | 'valueSelects',
            ) => void,
        ) => {
            const args = config?.calculator?.definition?.args;
            return <CalculatorDate arg={args[argsKey]} onInputChange={onChange} />;
        },
    };

    getReadOnlyFields = () => {
        const { config } = this.state;
        const readOnlyFields =
            config?.calculator?.definition?.equations?.reduce(
                (previousValue: string[], fnObj) => [
                    ...previousValue,
                    ...Object.keys(fnObj.f || []),
                ],
                [],
            ) || [];
        return uniq(readOnlyFields);
    };

    handleNextFocus = (index: number, subIndex: number) => {
        const currentActiveRow = this.REF_MAP?.[index]?.[subIndex];
        try {
            currentActiveRow?.focus();
        } catch (error) {
            console.error(error);
        }
    };

    render() {
        const { config, error, results, tableActiveSection } = this.state;
        const args = config?.calculator?.definition?.args;
        const argsKeys = Object.entries(args || {})
            .filter((i) => !i[1].ekaHidden)
            .map((i) => i[0]);
        const readOnlyField = this.getReadOnlyFields();

        return (
            <Fragment>
                <div className="w-60p bg-white flex flex-col rounded-4">
                    <p className="text-16 font-700 px-16 py-10 border-b border-darwin-neutral-100">
                        {config?.calculator?.text?.title}
                    </p>
                    <div className="flex-1 overflow-auto p-16 space-y-16">
                        {argsKeys?.map((key, index) => {
                            const isReadOnly = readOnlyField.find((item) => item === key);
                            const isValueSelect = args?.[key]?.valueSelectId;
                            const isDate = args?.[key]?.fieldType === 'DATE';

                            return (
                                <Fragment>
                                    {!isReadOnly && !args?.[key]?.ekaHidden && (
                                        <div className="flex items-center justify-between space-x-16">
                                            <p>{args?.[key]?.label}</p>
                                            <div
                                                className={`flex items-center justify-end w-40p ${
                                                    isValueSelect || isDate
                                                        ? ''
                                                        : 'border border-darwin-neutral-100 bg-darwin-neutral-50 text-darwin-neutral-600'
                                                } rounded-4 cursor-pointer`}
                                            >
                                                {config &&
                                                    this.INPUT_TYPE_CONFIG[
                                                        args?.[key]?.fieldType || ''
                                                    ](
                                                        key,
                                                        config,
                                                        this.onChange,
                                                        index,
                                                        args?.[key]?.unitSwitcherReversed
                                                            ? args[key].suffix ||
                                                                  args[key].suffixEnd
                                                            : undefined,
                                                    )}
                                                {!args?.[key]?.unitSwitcherReversed &&
                                                    (args?.[key]?.suffix ||
                                                        args?.[key]?.suffixEnd) && (
                                                        <p className="px-12 whitespace-nowrap">
                                                            {args[key].suffix ||
                                                                args[key].suffixEnd}
                                                        </p>
                                                    )}
                                            </div>
                                        </div>
                                    )}
                                    {!isReadOnly && !args?.[key]?.ekaHidden && error[key] && (
                                        <p className="w-full text-right text-red-500">
                                            {error[key]}
                                        </p>
                                    )}
                                </Fragment>
                            );
                        })}
                    </div>

                    <div className="w-full flex pb-24 px-16 justify-end">
                        <button
                            className="primary-btn px-50 py-8 btn btn--small space-x-8"
                            onClick={this.onSaveInDb}
                        >
                            <span className="w-full text-center">Copy to Pad</span>
                        </button>
                    </div>
                </div>
                <div className="w-40p min-w-40p flex flex-col space-y-12">
                    <div className="bg-white rounded-4 min-h-136">
                        <p className="text-16 font-700 px-16 py-10 border-b border-darwin-neutral-100">
                            Result
                        </p>
                        {results?.map((result, i) => {
                            if (!result.value.qt && Number(result.value.qt) !== 0) {
                                return null;
                            }

                            if (args?.[readOnlyField[i]].fieldType === 'DATE') {
                                return (
                                    <div className="flex flex-col">
                                        {results.length > 0 ? (
                                            <div className="px-16 pt-8 text-12">
                                                {args?.[readOnlyField[i]].label}
                                            </div>
                                        ) : null}
                                        <p
                                            className="text-darwin-accent-bright-blue-800 px-16 py-8 cursor-pointer"
                                            onClick={this.onSaveInDb}
                                        >
                                            <AntTooltip content={'Copy to Pad'} placement={'right'}>
                                                <span className="text-57 font-700">
                                                    {' '}
                                                    {result.value.qt ? result.value.qt : ''}{' '}
                                                    {args?.[readOnlyField[i]]?.suffixEnd}
                                                </span>
                                            </AntTooltip>
                                        </p>
                                    </div>
                                );
                            }

                            const value = +requiredMethodsForEquationEvaluation.roundOff(
                                Number(result.value.qt),
                            );

                            return (
                                <div className="flex flex-col px-16 py-8">
                                    {results.length > 0 ? (
                                        <div className="text-14 font-700">
                                            {args?.[readOnlyField[i]].label}:
                                        </div>
                                    ) : null}
                                    <p
                                        className="text-darwin-accent-bright-blue-800 cursor-pointer"
                                        onClick={this.onSaveInDb}
                                    >
                                        <AntTooltip content={'Copy to Pad'} placement={'right'}>
                                            <span className="text-57 font-700">
                                                {' '}
                                                {isNaN(Number(value)) ? '-' : value}{' '}
                                                {args?.[readOnlyField[i]]?.unitDefault}{' '}
                                                {args?.[readOnlyField[i]]?.suffixEnd}
                                            </span>
                                        </AntTooltip>
                                    </p>
                                </div>
                            );
                        })}
                        {!results?.length && (
                            <p className="text-darwin-neutral-600 px-16 py-10">
                                Enter values for calculations
                            </p>
                        )}
                        <p className="px-16 py-10" id="result-description"></p>
                    </div>

                    {(config?.calculator?.ekaInterpretation || config?.calculator?.ekaHowToUse) && (
                        <div className="flex flex-col bg-white px-16 pb-10 rounded-4 overflow-hidden flex-1">
                            <div className="sticky top-0 flex bg-white">
                                {config?.calculator?.ekaInterpretation && (
                                    <button
                                        className={`flex-1 ${
                                            tableActiveSection === 'interpretation'
                                                ? 'calc-active-btn'
                                                : 'border-b border-darwin-neutral-100'
                                        } flex items-center justify-center space-x-8 px-16 py-10`}
                                        onClick={this.setInterpretationSectionActive}
                                    >
                                        <svg
                                            className="min-w-12 max-w-12 min-h-12 max-h-12 relative top-1 fill-darwin-neutral-900"
                                            viewBox="0 0 14 11"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M12.8594 9.27344C13.2344 9.92969 12.7656 10.75 12.0156 10.75H1.98438C1.21094 10.75 0.742188 9.92969 1.14062 9.27344L6.13281 0.742188C6.53125 0.0859375 7.49219 0.0859375 7.84375 0.742188L12.8594 9.27344Z" />
                                        </svg>
                                        <span>Interpretation</span>
                                    </button>
                                )}
                                {config?.calculator?.ekaHowToUse && (
                                    <button
                                        className={`flex-1 ${
                                            tableActiveSection === 'how-to-use'
                                                ? 'calc-active-btn'
                                                : 'border-b border-darwin-neutral-100'
                                        } flex items-center justify-center space-x-8 px-16 py-10`}
                                        onClick={this.setHowToUseSectionActive}
                                    >
                                        <svg
                                            className="w-8 h-12 relative top-1 fill-darwin-neutral-900"
                                            viewBox="0 0 8 11"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M5.03125 0.273438H2.5C1.25781 0.273438 0.25 1.28125 0.25 2.52344C0.25 2.92188 0.578125 3.25 1 3.25C1.39844 3.25 1.75 2.89844 1.75 2.52344C1.75 2.10156 2.07812 1.77344 2.5 1.77344H5.03125C5.6875 1.77344 6.25 2.3125 6.25 2.96875C6.25 3.4375 5.99219 3.85938 5.52344 4.09375L3.22656 5.42969C2.99219 5.57031 2.875 5.80469 2.875 6.0625V7C2.875 7.42188 3.20312 7.75 3.625 7.75C4.02344 7.75 4.375 7.42188 4.375 7V6.50781L6.25 5.40625C7.16406 4.9375 7.75 4 7.75 2.96875C7.75 1.46875 6.53125 0.273438 5.03125 0.273438ZM3.625 8.875C3.08594 8.875 2.6875 9.29688 2.6875 9.8125C2.6875 10.3516 3.08594 10.75 3.625 10.75C4.14062 10.75 4.5625 10.3281 4.5625 9.8125C4.5625 9.32031 4.14062 8.875 3.625 8.875Z" />
                                        </svg>
                                        <span>How to use</span>
                                    </button>
                                )}
                            </div>

                            {tableActiveSection === 'how-to-use' && config && (
                                <div
                                    className="py-16 flex-1 overflow-auto"
                                    dangerouslySetInnerHTML={{
                                        __html: config.calculator?.ekaHowToUse,
                                    }}
                                ></div>
                            )}

                            {tableActiveSection === 'interpretation' && config ? (
                                config?.calculator?.ekaInterpretation?.type === 'html' ? (
                                    <div
                                        className="py-16 flex-1 overflow-auto"
                                        dangerouslySetInnerHTML={{
                                            __html: config.calculator?.ekaInterpretation.info,
                                        }}
                                    ></div>
                                ) : (
                                    <div className="overflow-hidden flex-1 mt-16">
                                        <CalculatorInterPretationTable
                                            ekaInterpretation={
                                                config?.calculator?.ekaInterpretation
                                            }
                                        />
                                    </div>
                                )
                            ) : null}
                        </div>
                    )}
                </div>
            </Fragment>
        );
    }
}

export default connect()(Calculator);
